import React from 'react'
import { useTable } from '@refinedev/antd'
import { useUpdate, useCreate } from '@refinedev/core'
import { Switch, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, type Stat } from '../../theme/listkit'
import { RefreshCw, ShieldCheck } from '../../components/icons'

interface FeatureFlag {
  key: string
  enabled: boolean
  description: string | null
  updated_at: string
}

/** Feature-flag keys owned by this curated page (excluded from the generic Feature Flags list). */
export const DEMO_FLAG_KEYS = ['demo_switcher_enabled'] as const

export const isDemoOwned = (key: string): boolean =>
  (DEMO_FLAG_KEYS as readonly string[]).includes(key)

interface CatalogEntry {
  key: string
  title: string
  note: string
  kind: 'master' | 'guard'
}

/**
 * Friendly catalog for the demo-switcher flag. Surfaced even when the row does
 * not exist yet (a missing row already means "off" to the app) — toggling it on
 * inserts the row. See docs/DEMO_MODE.md.
 */
const DEMO_CATALOG: CatalogEntry[] = [
  {
    key: 'demo_switcher_enabled',
    title: 'Demo switcher (master)',
    note: 'Reveal the demo/prod environment toggle in the mobile app (Settings → Developer). Set this ON in PRODUCTION — a prod-pointing app reads it to decide whether to show the toggle. Default off. The "DEMO DATA" banner then shows automatically whenever a user flips it to the demo backend.',
    kind: 'master',
  },
]

/**
 * Demo Mode — a curated surface for the demo-switcher flags. The mobile app can
 * hot-swap between the seeded demo backend and production for live demos; these
 * flags gate that switcher. The known flags always show here (even before a row
 * exists) so they are discoverable; toggling a not-yet-created flag inserts the
 * row. The mobile app subscribes to feature_flags Realtime, so changes are live.
 */
export function DemoSettingsPage() {
  // feature_flags is a small, domain-bounded table; an explicit bounded pageSize
  // (200 >> the real key count) keeps the whole flag set on one page for the
  // demo-key lookup below without ever issuing an unbounded fetch.
  const { tableProps } = useTable<FeatureFlag>({
    resource: 'feature_flags',
    syncWithLocation: false,
    pagination: { pageSize: 200 },
    meta: { idColumnName: 'key' },
  })
  const { mutate: update } = useUpdate()
  const { mutate: create } = useCreate()

  const rows = (tableProps.dataSource ?? []) as FeatureFlag[]
  const byKey = new Map(rows.map((r) => [r.key, r]))
  const isLoading = tableProps.loading as boolean

  const items = DEMO_CATALOG.map((catalog) => ({ catalog, row: byKey.get(catalog.key) ?? null }))
  const masterOn = byKey.get('demo_switcher_enabled')?.enabled ?? false

  function setFlag(entry: CatalogEntry, row: FeatureFlag | null, enabled: boolean) {
    if (row) {
      update({ resource: 'feature_flags', id: entry.key, values: { enabled }, meta: { idColumnName: 'key' } })
    } else {
      // First touch — create the row (a missing row already reads as its default).
      create({
        resource: 'feature_flags',
        values: { key: entry.key, enabled, description: entry.note },
        meta: { idColumnName: 'key' },
      })
    }
  }

  const stats: Stat[] = [
    { label: 'Demo switcher', value: masterOn ? 'On' : 'Off', icon: RefreshCw, tone: masterOn ? 'green' : 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'System · Feature Flags',
        description:
          'The mobile app can hot-swap between the seeded demo backend and production for live demos — without polluting production. Turn it on here (set it in PROD so a prod-pointing app reveals it); members then get a simple on/off toggle in Settings → Developer, and a persistent "DEMO DATA" banner shows whenever they are on the demo backend. Changes take effect within seconds — no deploy required. See docs/DEMO_MODE.md.',
      }}
      stats={stats}
      statColumns={2}
    >
      <SectionCard title="Demo environment switcher">
        {isLoading ? (
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, padding: '24px 0' }}>
            Loading…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {items.map(({ catalog, row }, idx) => {
              const isMaster = catalog.kind === 'master'
              const enabled = row?.enabled ?? false
              return (
                <div
                  key={catalog.key}
                  data-testid={`demo-row-${catalog.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    ...(isMaster
                      ? {
                          background: 'rgba(196,151,49,.06)',
                          borderRadius: 10,
                          padding: '18px 14px',
                          marginBottom: 8,
                          border: '1px solid rgba(196,151,49,.22)',
                        }
                      : {
                          padding: '16px 0',
                          borderBottom: idx < items.length - 1 ? `1px solid ${brand.cardLine}` : 'none',
                        }),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      {isMaster ? <RefreshCw size={18} color={brand.gold} /> : <ShieldCheck size={18} color={brand.jaguar} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: isMaster ? 700 : 600,
                          fontSize: isMaster ? 14 : 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {catalog.title}
                        {!row && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontFamily: brand.fSans,
                              fontWeight: 700,
                              fontSize: 10,
                              color: brand.sub,
                              background: 'rgba(0,0,0,.05)',
                              padding: '2px 7px',
                              borderRadius: 999,
                              verticalAlign: 'middle',
                            }}
                          >
                            NOT SET
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }}>
                        {catalog.note}
                      </div>
                      {row?.updated_at && (
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 500,
                            fontSize: 11,
                            color: brand.sub,
                            marginTop: 4,
                            opacity: 0.65,
                          }}
                        >
                          Last changed {new Date(row.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <Tooltip title={enabled ? `Disable "${catalog.title}"` : `Enable "${catalog.title}"`}>
                      <Switch
                        checked={enabled}
                        onChange={(checked) => setFlag(catalog, row, checked)}
                        style={isMaster ? { background: enabled ? brand.gold : undefined } : undefined}
                      />
                    </Tooltip>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
